import { shopifyUrl } from "./utilities.js";

let cartInitialized = false;
let eventListenersAdded = false;

function setupCartDrawer() {
  // ==============================
  // Elements
  // ==============================
  const drawer = document.getElementById("CartDrawer");
  const overlay = document.getElementById("CartDrawerOverlay");
  const content = document.getElementById("CartDrawerItems");
  const cartCountEl = document.getElementById("CartCount");
  const cartIcon = document.getElementById("CartIcon");

  // ==============================
  // Drawer Controls
  // ==============================
  function toggleDrawer(show) {
    drawer?.classList.toggle("cart-drawer--visible", show);
    drawer?.classList.toggle("cart-drawer--hidden", !show);
    overlay?.classList.toggle("cart-drawer-overlay--show", show);
    drawer?.setAttribute("aria-hidden", String(!show));
    if (show) drawer?.focus();
  }

  window.CartDrawer = {
    open: () => toggleDrawer(true),
    close: () => toggleDrawer(false),
    refresh: refreshCartDrawer,
    reinit: reinitializeCart,
  };

  // ==============================
  // Cart Refresh - Fetch cart data and drawer HTML
  // ==============================
  async function refreshCartDrawer() {
    if (!content) return;

    const oldBar = content.querySelector(".c-free-shipping__bar");
    const oldWidth = oldBar ? oldBar.style.width : null;

    try {
      // Fetch both cart data and drawer HTML in parallel
      const [cartResponse, drawerResponse] = await Promise.all([
        fetch(shopifyUrl("cart.js")),
        fetch(shopifyUrl("cart?view=drawer")),
      ]);

      const cart = await cartResponse.json();
      const html = await drawerResponse.text();

      content.innerHTML = html;

      // Get actual item count from cart.js API
      const count = cart.item_count || 0;
      updateCartCount(count);

      bindDiscountForm();
      animateFreeShippingBar(oldWidth);

      document.dispatchEvent(new Event("cart:refreshed"));
    } catch (err) {
      console.error("Cart refresh error:", err);
    }
  }

  function animateFreeShippingBar(oldWidth) {
    const bar = content.querySelector(".c-free-shipping__bar");
    if (!bar) return;

    const target = parseFloat(bar.dataset.progress) || 0;
    if (oldWidth === null) {
      bar.style.width = target + "%";
      return;
    }

    bar.style.width = oldWidth;
    requestAnimationFrame(() => {
      bar.style.width = target + "%";
    });
  }

  function updateCartCount(count) {
    if (!cartCountEl) return;
    cartCountEl.textContent = count;
    cartCountEl.style.display = count > 0 ? "inline" : "none";
  }

  // ==============================
  // Discount Handling (Shopify Plus Multi-Code)
  // ==============================

  function bindDiscountForm() {
    const discountForm = document.querySelector("#discount-form");
    const discountInput = document.querySelector("#discount-code");
    const discountMessage = document.querySelector("#discount-message");
    if (!discountForm || !discountInput) return;

    discountForm.addEventListener("submit", async (e) => {
      e.preventDefault();
      const code = discountInput.value.trim();
      if (!code) {
        setDiscountMessage("Please enter a discount code.", "red");
        return;
      }

      try {
        const cart = await fetch(shopifyUrl("cart.js")).then((r) => r.json());

        const existingCodes =
          cart.discount_codes?.map((dc) => dc.code.toLowerCase()) || [];

        // Already applied?
        if (existingCodes.includes(code.toLowerCase())) {
          setDiscountMessage(`Discount "${code}" is already applied.`, "green");
          return;
        }

        const success = await applyDiscountCode(code, existingCodes);

        if (success) {
          setDiscountMessage(`Discount "${code}" applied!`, "green");
          await refreshCartDrawer();
        } else {
          setDiscountMessage(
            `Discount "${code}" is invalid or not applicable.`,
            "red",
          );
        }
      } catch (err) {
        console.error("Discount apply error:", err);
        setDiscountMessage("Error applying discount.", "red");
      }
    });

    function setDiscountMessage(text, color) {
      if (!discountMessage) return;
      discountMessage.textContent = text;
      discountMessage.style.color = color;
    }
  }

  // Apply new discount (Shopify Plus supports comma-separated codes)
  async function applyDiscountCode(newCode, existing = []) {
    const allCodes = [...existing, newCode];

    const formData = new FormData();
    formData.append("discount", allCodes.join(","));

    const res = await fetch(shopifyUrl("cart/update.js"), {
      method: "POST",
      body: formData,
    });

    const cart = await res.json();

    return cart.discount_codes?.some(
      (dc) => dc.code.toLowerCase() === newCode.toLowerCase(),
    );
  }

  // Remove ONE discount code
  async function removeDiscountCode(codeToRemove) {
    const cart = await fetch(shopifyUrl("cart.js")).then((r) => r.json());

    const remaining = cart.discount_codes
      .map((dc) => dc.code)
      .filter((c) => c.toLowerCase() !== codeToRemove.toLowerCase());

    const formData = new FormData();
    formData.append("discount", remaining.join(","));

    const res = await fetch(shopifyUrl("cart/update.js"), {
      method: "POST",
      body: formData,
    });

    return res.json();
  }

  // ==============================
  // Cart Item Updates - Single request then refresh
  // ==============================
  async function updateItem(line, quantity) {
    try {
      const res = await fetch(shopifyUrl("cart/change.js"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ line, quantity }),
      });

      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const response = await res.json();
      await refreshCartDrawer();

      return response;
    } catch (err) {
      console.error("Cart update failed:", err);
      alert("Failed to update cart. Please try again.");
      throw err;
    }
  }

  // ==============================
  // Event Listeners
  // ==============================

  // Only add event listeners once
  if (!eventListenersAdded) {
    eventListenersAdded = true;

    // Add to Cart - No redundant fetches
    document.addEventListener("submit", async (e) => {
      const form = e.target.closest(".product-form form");
      if (!form) return;
      e.preventDefault();

      const submitBtn = form.querySelector('button[type="submit"]');
      if (submitBtn?.disabled) return;

      if (submitBtn) {
        submitBtn.disabled = true;
        const originalText = submitBtn.textContent;
        submitBtn.textContent = "Adding...";

        setTimeout(() => {
          submitBtn.disabled = false;
          submitBtn.textContent = originalText;
        }, 2000);
      }

      try {
        const response = await fetch(shopifyUrl("cart/add.js"), {
          method: "POST",
          body: new FormData(form),
          headers: { Accept: "application/json" },
        });

        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        await response.json();

        // Open drawer and refresh once
        window.CartDrawer.open();
        await refreshCartDrawer();

        document.dispatchEvent(
          new CustomEvent("cart:itemAdded", { detail: { form } }),
        );
      } catch (err) {
        console.error("Add to cart error:", err);
        alert("Failed to add to cart. Please try again.");
      }
    });

    // Open/Close Drawer
    cartIcon?.addEventListener("click", (e) => {
      e.preventDefault();
      const isVisible = drawer?.classList.contains("cart-drawer--visible");
      if (!isVisible) {
        toggleDrawer(true);
        refreshCartDrawer();
      }
    });
    overlay?.addEventListener("click", () => toggleDrawer(false));
    document
      .querySelectorAll("[data-close-drawer]")
      .forEach((el) => el.addEventListener("click", () => toggleDrawer(false)));

    // Inside Drawer Actions
    content?.addEventListener("click", async (e) => {
      const removeBtn = e.target.closest("[data-remove-line]");
      const discountRemoveBtn = e.target.closest(".c-cart-discount__remove");

      if (removeBtn && !removeBtn.disabled) {
        const line = parseInt(removeBtn.dataset.removeLine, 10);
        const bundleId = removeBtn.dataset.bundleId;
        const cartItem = removeBtn.closest(".c-cart-item");

        removeBtn.disabled = true;
        if (cartItem) cartItem.classList.add("is-removing");

        try {
          if (bundleId) {
            await removeBundle(bundleId);
          } else {
            await updateItem(line, 0);
          }
        } catch (err) {
          removeBtn.disabled = false;
          if (cartItem) cartItem.classList.remove("is-removing");
        }
        return;
      }

      if (discountRemoveBtn) {
        e.preventDefault();
        const code = discountRemoveBtn.dataset.code;

        try {
          await removeDiscountCode(code);
          const msg = content.querySelector("#discount-message");
          if (msg) {
            msg.textContent = `Discount "${code}" removed.`;
            msg.style.color = "green";
          }
          await refreshCartDrawer();
        } catch (err) {
          console.error("Failed to remove discount:", err);
          const msg = content.querySelector("#discount-message");
          if (msg) {
            msg.textContent = "Failed to remove discount.";
            msg.style.color = "red";
          }
        }
        return;
      }
    });

    async function removeBundle(bundleId) {
      try {
        const cart = await fetch(shopifyUrl("cart.js")).then((r) => r.json());
        const linesToRemove = cart.items
          .map((item, index) => {
            const props = item.properties || {};
            const matchesBundle =
              props._bundle_id === bundleId || props["Box ID"] === bundleId;
            return matchesBundle ? index + 1 : null;
          })
          .filter(Boolean)
          .sort((a, b) => b - a);

        // Remove items sequentially
        for (const line of linesToRemove) {
          const res = await fetch(shopifyUrl("cart/change.js"), {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ line, quantity: 0 }),
          });

          if (!res.ok) throw new Error(`HTTP ${res.status}`);
          await res.json();
        }

        await refreshCartDrawer();
      } catch (err) {
        console.error("Failed to remove bundle:", err);
        alert("Failed to remove bundle. Please try again.");
        throw err;
      }
    }

    // Quantity Change
    content?.addEventListener("change", async (e) => {
      const input = e.target.closest('input[name="quantity"]');
      if (!input) return;

      const form = input.closest(".cart-item-update-form");
      const line = parseInt(form.dataset.line, 10);
      const cartItem = form.closest(".c-cart-item");

      let quantity = Math.max(0, parseInt(input.value, 10) || 0);
      input.value = quantity;

      const buttons = form.querySelectorAll("button");
      buttons.forEach((btn) => (btn.disabled = true));
      if (cartItem) cartItem.classList.add("is-updating");

      try {
        await updateItem(line, quantity);
      } catch (err) {
        // Error already handled in updateItem
      } finally {
        buttons.forEach((btn) => (btn.disabled = false));
        if (cartItem) cartItem.classList.remove("is-updating");
      }
    });

    // Manual Quantity Form Submit
    content?.addEventListener("submit", async (e) => {
      if (!e.target.classList.contains("cart-item-update-form")) return;
      e.preventDefault();

      const form = e.target;
      const line = parseInt(form.dataset.line, 10);
      const input = form.querySelector('input[name="quantity"]');
      const quantity = Math.max(0, parseInt(input.value, 10) || 0);

      const btn = form.querySelector('button[type="submit"]');
      btn && (btn.disabled = true);

      await updateItem(line, quantity);
      btn && (btn.disabled = false);
    });
  } // end eventListenersAdded guard

  // ==============================
  // PENDING DISCOUNTS
  // ==============================
  function renderPendingDiscounts(cart) {
    const container = document.getElementById("pending-discounts");
    if (!container) return;

    console.log({ cart });

    // Only the codes Shopify says are not applied yet
    const pending = cart.discount_codes.filter((dc) => !dc.applicable);

    container.innerHTML = ""; // Clear before adding

    pending.forEach((dc) => {
      const div = document.createElement("div");
      div.className = "c-cart-discount pending-discount";

      div.innerHTML = `
      <div class="c-cart-discount-pending__item">
        <small class="c-cart-discount__note">
          ${window.Translations.pendingDiscountNote}
        </small>
        <div class="c-product-badge  c-cart-discount-tag">
          ${dc.code} - (${window.Translations.pendingDiscountLabel})

          <button class="c-cart-discount__remove"  aria-label="Remove ${dc.code}" data-code="${dc.code}">
            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
              <mask id="mask0_1235_2058" style="mask-type:alpha" maskUnits="userSpaceOnUse" x="0" y="0" width="16" height="16">
                <rect width="16" height="16" fill="#D9D9D9"/>
              </mask>
              <g mask="url(#mask0_1235_2058)">
                <path d="M4.26695 12.4358L3.56445 11.7333L7.29779 8L3.56445 4.26666L4.26695 3.56416L8.00029 7.2975L11.7336 3.56416L12.4361 4.26666L8.70279 8L12.4361 11.7333L11.7336 12.4358L8.00029 8.7025L4.26695 12.4358Z" fill="#1C355E"/>
              </g>
            </svg>
          </button>
        </div>
      </div>

      
    `;

      container.appendChild(div);
    });
  }
}

function reinitializeCart() {
  console.log("Reinitializing cart after page swap...");
  setupCartDrawer();
}

function initCart() {
  document.addEventListener("DOMContentLoaded", () => {
    if (cartInitialized) return;
    cartInitialized = true;
    setupCartDrawer();
  });

  // Also listen for page:loaded event from AJAX page swaps
  document.addEventListener("page:loaded", () => {
    console.log("page:loaded event detected, reinitializing cart...");
    reinitializeCart();
  });
}

initCart();

export { initCart, reinitializeCart };
